/**
 * Settings service layer.
 * Manages user notification preferences and privacy settings stored in
 * Firestore subcollections: users/{userId}/settings/notifications
 * and users/{userId}/settings/privacy.
 *
 * Also handles theme preference persistence, date format preference,
 * and account deletion.
 */
import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  serverTimestamp,
} from 'firebase/firestore';
import {
  reauthenticateWithCredential,
  EmailAuthProvider,
  updatePassword as firebaseUpdatePassword,
  deleteUser,
} from 'firebase/auth';
import { db, auth } from './firebase';
import { handleServiceError } from '../utils/error-handler.util';

const USERS_COLLECTION = 'users';
const SETTINGS_SUBCOLLECTION = 'settings';

/**
 * Default notification preferences. All enabled by default.
 */
const DEFAULT_NOTIFICATION_PREFS = {
  likes: true,
  comments: true,
  newFollowers: true,
  achievementUnlocks: true,
  partnerReminders: true,
  streakMilestones: true,
  workoutShares: true,
  muteAll: false,
};

/**
 * Default privacy settings.
 */
const DEFAULT_PRIVACY_SETTINGS = {
  profileVisibility: 'public', // 'public' | 'followers_only'
  showWorkoutStats: true,
  showBodyStats: true,
};

// ── Notification Preferences ──────────────────────────────────────────────────

/**
 * Get notification preferences for a user.
 * Creates default doc if none exists (lazy initialization).
 * @param {string} userId
 * @returns {Promise<Object>}
 */
export const getNotificationPreferences = async (userId) => {
  try {
    const docRef = doc(db, USERS_COLLECTION, userId, SETTINGS_SUBCOLLECTION, 'notifications');
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      return { ...DEFAULT_NOTIFICATION_PREFS, ...docSnap.data() };
    }

    // Lazy-create default doc
    await setDoc(docRef, { ...DEFAULT_NOTIFICATION_PREFS, createdAt: serverTimestamp() });
    return { ...DEFAULT_NOTIFICATION_PREFS };
  } catch (error) {
    handleServiceError(error, 'Get notification preferences', 'Failed to load notification preferences.');
  }
};

/**
 * Update notification preferences.
 * @param {string} userId
 * @param {Object} prefs - Partial notification preferences to merge.
 * @returns {Promise<Object>}
 */
export const updateNotificationPreferences = async (userId, prefs) => {
  try {
    const docRef = doc(db, USERS_COLLECTION, userId, SETTINGS_SUBCOLLECTION, 'notifications');
    await setDoc(docRef, { ...prefs, updatedAt: serverTimestamp() }, { merge: true });
    const updatedSnap = await getDoc(docRef);
    return { ...DEFAULT_NOTIFICATION_PREFS, ...updatedSnap.data() };
  } catch (error) {
    handleServiceError(error, 'Update notification preferences', 'Failed to update notification preferences.');
  }
};

// ── Privacy Settings ──────────────────────────────────────────────────────────

/**
 * Get privacy settings for a user.
 * Creates default doc if none exists.
 * @param {string} userId
 * @returns {Promise<Object>}
 */
export const getPrivacySettings = async (userId) => {
  try {
    const docRef = doc(db, USERS_COLLECTION, userId, SETTINGS_SUBCOLLECTION, 'privacy');
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      return { ...DEFAULT_PRIVACY_SETTINGS, ...docSnap.data() };
    }

    // Lazy-create default doc
    await setDoc(docRef, { ...DEFAULT_PRIVACY_SETTINGS, createdAt: serverTimestamp() });
    return { ...DEFAULT_PRIVACY_SETTINGS };
  } catch (error) {
    handleServiceError(error, 'Get privacy settings', 'Failed to load privacy settings.');
  }
};

/**
 * Update privacy settings.
 * @param {string} userId
 * @param {Object} settings - Partial privacy settings to merge.
 * @returns {Promise<Object>}
 */
export const updatePrivacySettings = async (userId, settings) => {
  try {
    const docRef = doc(db, USERS_COLLECTION, userId, SETTINGS_SUBCOLLECTION, 'privacy');
    await setDoc(docRef, { ...settings, updatedAt: serverTimestamp() }, { merge: true });
    const updatedSnap = await getDoc(docRef);
    return { ...DEFAULT_PRIVACY_SETTINGS, ...updatedSnap.data() };
  } catch (error) {
    handleServiceError(error, 'Update privacy settings', 'Failed to update privacy settings.');
  }
};

// ── Theme & Date Preferences (stored on user profile) ─────────────────────────

/**
 * Save theme preference to user profile.
 * @param {string} userId
 * @param {'light' | 'dark' | 'system'} theme
 */
export const updateThemePreference = async (userId, theme) => {
  try {
    const docRef = doc(db, USERS_COLLECTION, userId);
    await updateDoc(docRef, { themePreference: theme, updatedAt: serverTimestamp() });
  } catch (error) {
    handleServiceError(error, 'Update theme preference', 'Failed to save theme preference.');
  }
};

/**
 * Save date format preference to user profile.
 * @param {string} userId
 * @param {'MM/DD/YYYY' | 'DD/MM/YYYY'} format
 */
export const updateDateFormat = async (userId, format) => {
  try {
    const docRef = doc(db, USERS_COLLECTION, userId);
    await updateDoc(docRef, { dateFormat: format, updatedAt: serverTimestamp() });
  } catch (error) {
    handleServiceError(error, 'Update date format', 'Failed to save date format preference.');
  }
};

// ── Password Change ───────────────────────────────────────────────────────────

/**
 * Change the current user's password after re-authentication.
 * @param {string} currentPassword
 * @param {string} newPassword
 */
export const changePassword = async (currentPassword, newPassword) => {
  try {
    const user = auth.currentUser;
    if (!user || !user.email) throw new Error('No authenticated user found.');

    const credential = EmailAuthProvider.credential(user.email, currentPassword);
    await reauthenticateWithCredential(user, credential);
    await firebaseUpdatePassword(user, newPassword);
  } catch (error) {
    handleServiceError(error, 'Change password');
  }
};

// ── Account Deletion ──────────────────────────────────────────────────────────

/**
 * Delete user account.
 * Marks the user document as deleted, then deletes the Firebase Auth account.
 * @param {string} userId
 * @param {string} currentPassword - Required for re-authentication.
 */
export const deleteAccount = async (userId, currentPassword) => {
  try {
    const user = auth.currentUser;
    if (!user || !user.email) throw new Error('No authenticated user found.');

    // Re-authenticate first
    const credential = EmailAuthProvider.credential(user.email, currentPassword);
    await reauthenticateWithCredential(user, credential);

    // Mark user doc as deleted (don't actually purge Firestore data)
    const userDocRef = doc(db, USERS_COLLECTION, userId);
    await updateDoc(userDocRef, {
      deleted: true,
      deletedAt: serverTimestamp(),
      displayName: 'Deleted User',
      bio: '',
      avatarUrl: '',
    });

    // Delete the Firebase Auth account
    await deleteUser(user);
  } catch (error) {
    handleServiceError(error, 'Delete account');
  }
};
